// DocumentoDrillDown — modal de 2 vistas para explorar documentos comerciales.
// Vista 1: lista de documentos filtrados por tipo.
// Vista 2: detalle del documento con líneas y cadena de trazabilidad.
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ArrowLeft, FileText, Receipt, Truck, Package, Loader2, Link2 } from 'lucide-react';
import { docsComercialesApi } from '../../services/api';

const fmtEur = (v) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(v || 0);
const fmtNum = (v) => v === null || v === undefined ? '—' : new Intl.NumberFormat('es-ES').format(v);

const TIPO_META = {
  oferta:   { label: 'Oferta',    icon: FileText, color: 'bg-sky-100 text-sky-700 border-sky-200' },
  pedido:   { label: 'Pedido',    icon: Package,  color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  albaran:  { label: 'Albarán',   icon: Truck,    color: 'bg-violet-100 text-violet-700 border-violet-200' },
  factura:  { label: 'Factura',   icon: Receipt,  color: 'bg-amber-100 text-amber-700 border-amber-200' },
};

const DocumentoDrillDown = ({ open, onOpenChange, tipoInicial, tituloInicial }) => {
  const [tipo, setTipo] = useState(tipoInicial);
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(false);
  const [seleccionadoId, setSeleccionadoId] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [cadena, setCadena] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);

  useEffect(() => { if (open) setTipo(tipoInicial); }, [open, tipoInicial]);

  useEffect(() => {
    if (!open || !tipo) return;
    setLoading(true); setSeleccionadoId(null); setDetalle(null); setCadena(null);
    docsComercialesApi.list({ tipo, limit: 100 })
      .then((r) => setLista(r.items || []))
      .catch(() => setLista([]))
      .finally(() => setLoading(false));
  }, [open, tipo]);

  const verDetalle = async (id) => {
    setSeleccionadoId(id);
    setLoadingDetalle(true);
    try {
      const [d, t] = await Promise.all([
        docsComercialesApi.detalle(id),
        docsComercialesApi.trazabilidad(id).catch(() => null),
      ]);
      setDetalle(d);
      setCadena(t);
    } finally { setLoadingDetalle(false); }
  };

  const volver = () => { setSeleccionadoId(null); setDetalle(null); setCadena(null); };

  if (!tipo) return null;
  const meta = TIPO_META[tipo];
  const TIcon = meta?.icon || FileText;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="dialog-drill-documento">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {seleccionadoId && <Button variant="ghost" size="sm" onClick={volver} className="p-1 h-7 w-7" data-testid="btn-drill-volver"><ArrowLeft className="w-4 h-4" /></Button>}
            <TIcon className="w-5 h-5 text-ink-500" />
            {seleccionadoId ? `${meta.label} ${detalle?.numero || ''}` : (tituloInicial || `Todos los ${meta.label.toLowerCase()}s`)}
          </DialogTitle>
          <DialogDescription>
            {seleccionadoId ? 'Detalle del documento con líneas y cadena completa de trazabilidad.' : 'Click sobre un documento para ver su detalle.'}
          </DialogDescription>
        </DialogHeader>

        {!seleccionadoId && (
          <>
            {loading ? (
              <div className="flex items-center gap-2 py-6 text-ink-500"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
            ) : lista.length === 0 ? (
              <div className="text-center py-8 text-ink-400" data-testid="drill-lista-empty">Sin documentos.</div>
            ) : (
              <div className="border border-ink-200 rounded-md divide-y divide-ink-100" data-testid="drill-lista">
                {lista.slice(0, 100).map((doc) => (
                  <button
                    key={doc.id}
                    onClick={() => verDetalle(doc.id)}
                    className="w-full text-left px-3 py-2 hover:bg-ink-50 flex items-center gap-3"
                    data-testid={`drill-row-${doc.numero}`}
                  >
                    <div className="font-mono text-xs text-ink-700 w-28 truncate">{doc.numero}</div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-ink-800 truncate">{doc.cliente_nombre_erp || doc.cliente_codigo_erp || '—'}</div>
                      <div className="text-xs text-ink-500">{doc.fecha || '—'} · {(doc.lineas?.length || 0)} líneas</div>
                    </div>
                    <div className="text-sm font-medium text-ink-900 shrink-0">{fmtEur(doc.importe_total)}</div>
                  </button>
                ))}
                {lista.length >= 100 && <div className="px-3 py-2 text-xs text-ink-400 italic">Mostrando 100 — usa los filtros del dashboard o la ficha de cliente para acotar.</div>}
              </div>
            )}
          </>
        )}

        {seleccionadoId && loadingDetalle && (
          <div className="flex items-center gap-2 py-6 text-ink-500"><Loader2 className="w-4 h-4 animate-spin" /> Cargando detalle…</div>
        )}

        {seleccionadoId && !loadingDetalle && detalle && (
          <div className="space-y-4">
            {/* Cabecera detalle */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 bg-ink-50 rounded-md border border-ink-200">
              <div>
                <div className="text-xs text-ink-500 uppercase tracking-wider">Número</div>
                <div className="text-sm font-mono font-semibold text-ink-800">{detalle.numero}</div>
              </div>
              <div>
                <div className="text-xs text-ink-500 uppercase tracking-wider">Fecha</div>
                <div className="text-sm font-medium">{detalle.fecha || '—'}</div>
              </div>
              <div>
                <div className="text-xs text-ink-500 uppercase tracking-wider">Cliente</div>
                <div className="text-sm font-medium truncate">{detalle.cliente_nombre_erp || detalle.cliente_codigo_erp || '—'}</div>
              </div>
              <div>
                <div className="text-xs text-ink-500 uppercase tracking-wider">Importe total</div>
                <div className="text-sm font-bold text-ink-900">{fmtEur(detalle.importe_total)}</div>
              </div>
            </div>

            {/* Cadena trazabilidad */}
            {cadena?.cadena?.length > 1 && (
              <div data-testid="drill-cadena">
                <h3 className="text-sm font-semibold text-ink-800 mb-2 flex items-center gap-2">
                  <Link2 className="w-4 h-4 text-brand-500" /> Cadena de trazabilidad ({cadena.longitud} documentos)
                </h3>
                <div className="flex items-center gap-2 overflow-x-auto pb-2">
                  {cadena.cadena.map((nodo, i) => {
                    const nmeta = TIPO_META[nodo.tipo];
                    const NIcon = nmeta?.icon || FileText;
                    const esActual = nodo.id === seleccionadoId;
                    return (
                      <React.Fragment key={nodo.id}>
                        <button
                          onClick={() => !esActual && verDetalle(nodo.id)}
                          disabled={esActual}
                          className={`shrink-0 px-3 py-2 rounded-md border min-w-[140px] text-left ${nmeta?.color || 'bg-ink-100'} ${esActual ? 'ring-2 ring-brand-400 ring-offset-1' : 'hover:opacity-80'}`}
                          data-testid={`drill-cadena-${nodo.tipo}`}
                        >
                          <div className="flex items-center gap-1.5 mb-1">
                            <NIcon className="w-3.5 h-3.5" />
                            <span className="text-xs uppercase font-semibold tracking-wide">{nmeta?.label}</span>
                          </div>
                          <div className="font-mono text-xs truncate">{nodo.numero}</div>
                          <div className="text-[10px] opacity-75">{fmtEur(nodo.importe_total)}</div>
                        </button>
                        {i < cadena.cadena.length - 1 && <span className="text-ink-300">›</span>}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Líneas del documento */}
            <div>
              <h3 className="text-sm font-semibold text-ink-800 mb-2">Líneas ({detalle.lineas?.length || 0})</h3>
              <div className="overflow-x-auto border border-ink-200 rounded-md">
                <table className="w-full text-sm">
                  <thead className="bg-ink-50">
                    <tr className="text-left text-ink-600 border-b border-ink-200">
                      <th className="px-3 py-2 font-medium">Artículo</th>
                      <th className="px-3 py-2 font-medium text-right">Cantidad</th>
                      <th className="px-3 py-2 font-medium text-right">Precio unit.</th>
                      <th className="px-3 py-2 font-medium text-right">Importe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detalle.lineas || []).map((l, i) => (
                      <tr key={i} className="border-b border-ink-100 last:border-0">
                        <td className="px-3 py-1.5">
                          <div className="font-mono text-xs text-ink-500">{l.articulo_codigo}</div>
                          <div className="text-ink-800 truncate max-w-xs">{l.articulo_descripcion || '—'}</div>
                        </td>
                        <td className="px-3 py-1.5 text-right">{fmtNum(l.cantidad)}</td>
                        <td className="px-3 py-1.5 text-right">{fmtEur(l.precio_unitario)}</td>
                        <td className="px-3 py-1.5 text-right font-medium">{fmtEur(l.importe_linea)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 text-xs">
              {detalle.cliente_codigo_erp && <Badge variant="outline">ERP {detalle.cliente_codigo_erp}</Badge>}
              {detalle.documento_origen_numero && <Badge variant="outline">Origen: {detalle.documento_origen_numero}</Badge>}
              {detalle.forma_pago && <Badge variant="outline">Pago: {detalle.forma_pago}</Badge>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DocumentoDrillDown;
